import { motion } from "framer-motion";
import { statePlayerSwitch } from "../store";
import { useEffect, useState, useRef, useLayoutEffect } from "react";
import { statePlayerPositionS } from "../store";
import { stateOrbitLocation, stateInAreaRooms, stateDevice, stateNavigation, stateWallPush } from "../store";
import { useSnapshot } from "valtio";
import Sound from "../Utils/Sound";
import { ROOM_CONFIG } from "./roomConfig";

// Mirror of the click→player switch in HeaderWaypoint.jsx: when returning to
// click-navi, the world state (mounted room, entry-room flags) must be synced
// back to whatever the waypoint state still points at — the player may have
// walked into a completely different room in the meantime, and without this
// the camera returns to a waypoint whose room content is unmounted (empty room).
const ROOM_INDEX = {
  ThemeM_MuseenDerRegion: 0, ThemeM_Holzarbeit: 1, ThemeM_Klima: 2,
  ThemeM_Salzabbau: 3, ThemeM_Handelswege: 4, ThemeM_Menschen: 5,
};

function syncWorldToWaypointAndReturn() {
  let roomId = stateNavigation.waypointSetId;
  while (roomId && ROOM_INDEX[roomId] === undefined) {
    roomId = ROOM_CONFIG[roomId]?.parentId;
  }
  const idx = ROOM_INDEX[roomId];
  if (idx !== undefined) {
    stateWallPush.activeThemeIndex = idx;
    stateWallPush.visibleThemeIndex = idx;
    stateInAreaRooms.inAreaRoomEntryRoom = false;
    stateInAreaRooms.inAreaRoom = true;
  } else {
    // Waypoint lives in the entry hall — restore the plain hall state.
    stateWallPush.activeThemeIndex = -1;
    stateWallPush.visibleThemeIndex = -1;
    stateInAreaRooms.inAreaRoomEntryRoom = true;
    stateInAreaRooms.inAreaRoom = false;
  }
  stateNavigation.isClickNavi = false;
}

export default function Header() {
  return <HeaderKon />;
}

function HeaderKon() {
  const [charClicked, setCharClicked] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isFp, setIsFp] = useState(false);
  const snapOrbitLoaction = useSnapshot(stateOrbitLocation)
  const snapPlayerSwitch = useSnapshot(statePlayerSwitch)
  const snapInAreaRooms = useSnapshot(stateInAreaRooms)
  const snapStateDevice = useSnapshot(stateDevice)
  const snapStateNavigation = useSnapshot(stateNavigation)
  const isCompactHeader = snapStateDevice.isPortrait || snapStateDevice.isHandy

  const salzzeitRef = useRef(null);
  const line2Ref = useRef(null);
  const line3Ref = useRef(null);

  // Justify the two small subtitle lines to exactly match SALZZEIT's rendered
  // width by stretching their letter-spacing — same approach as HeaderWaypoint.jsx.
  useLayoutEffect(() => {
    let cancelled = false;
    const justify = () => {
      const salzzeitEl = salzzeitRef.current;
      if (!salzzeitEl) return;
      salzzeitEl.style.width = "max-content";
      const targetWidth = salzzeitEl.offsetWidth;
      if (!targetWidth) return;
      [line2Ref, line3Ref].forEach((ref) => {
        const el = ref.current;
        if (!el) return;
        el.style.width = "max-content";
        el.style.letterSpacing = "0px";
        const naturalWidth = el.offsetWidth;
        const gaps = Math.max((el.textContent || "").length, 1);
        let extra = Math.max((targetWidth - naturalWidth) / gaps, 0);
        el.style.letterSpacing = `${extra}px`;
        let guard = 0;
        while (el.offsetWidth > targetWidth && extra > 0 && guard < 30) {
          extra = Math.max(extra - 0.2, 0);
          el.style.letterSpacing = `${extra}px`;
          guard++;
        }
      });
    };

    justify();
    document.fonts?.ready?.then(() => { if (!cancelled) justify(); });
    const raf = requestAnimationFrame(justify);
    const timeouts = [50, 150, 300, 600, 1000].map((ms) =>
      setTimeout(() => { if (!cancelled) justify(); }, ms)
    );
    window.addEventListener("resize", justify);
    return () => {
      cancelled = true;
      cancelAnimationFrame(raf);
      timeouts.forEach(clearTimeout);
      window.removeEventListener("resize", justify);
    };
  }, [isCompactHeader]);

  const {
    playClickSound,
  } = Sound();

  function handleCharClick() {
    playClickSound()
    if (!statePlayerSwitch.charSwitchClicked && !stateOrbitLocation.lerpRuns ) {
      statePlayerSwitch.charSwitchClicked = true;
      setCharClicked(true);
    } else if(!stateOrbitLocation.lerpRuns) {
      statePlayerSwitch.charSwitchClicked = false;
      setCharClicked(false);
    }
  }

  useEffect(()=>{
   if(!stateOrbitLocation.lerpRuns && !snapPlayerSwitch.charSwitchClicked) {
      setCharClicked(false);
    }
  },[snapPlayerSwitch.charSwitchClicked])

  useEffect(() => {
    if (snapStateNavigation.isClickNavi) {
      stateNavigation.waypointListOpen = false;
    }
  }, [snapStateNavigation.isClickNavi]);

  function handleFPClick() {
    setIsFp(!isFp);
    playClickSound()
    if(!isFp){
      statePlayerPositionS.fpPlayer=true
    }
    else{
      statePlayerPositionS.fpPlayer=false
    }
  }

  function toggleFullscreen() {
    playClickSound()
    if (!isFullscreen) {
      const elem = document.documentElement;
      if (elem.requestFullscreen) {
        elem.requestFullscreen();
      } else if (elem.mozRequestFullScreen) {
        elem.mozRequestFullScreen();
      } else if (elem.webkitRequestFullscreen) {
        elem.webkitRequestFullscreen();
      } else if (elem.msRequestFullscreen) {
        elem.msRequestFullscreen();
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if (document.mozCancelFullScreen) {
        document.mozCancelFullScreen();
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      } else if (document.msExitFullscreen) {
        document.msExitFullscreen();
      }
    }
    setIsFullscreen(!isFullscreen);
  }

  const circleButtonClass = `flex items-center justify-center rounded-full border-2 cursor-pointer transition-all
    ${isCompactHeader ? "w-11 h-11" : "w-14 h-14"}`;
  const circleImgClass = isCompactHeader ? "w-6 h-6" : "w-8 h-8";

  return (
    <motion.div
      className="absolute top-0 left-0 w-full z-50 select-none"
      initial={{ opacity: 0, y: -100 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ type: "spring", duration: 1.8, delay: 2 }}
    >

      <header className={`flex justify-between items-center ${isCompactHeader ? "pl-2 pr-1 py-2" : "px-8 py-4"}`}>
        {/* Logo (top-left) */}
        <div className={`flex items-center pointer-events-auto ${isCompactHeader ? "space-x-2" : "space-x-4"}`}>
          <img
            src="../UI/IconMsueumHeader.svg"
            alt="Museum Icon"
            className={`opacity-80 ${isCompactHeader ? "w-10" : "w-14"}`}
            draggable="false"
          />
          <div>
            <div ref={salzzeitRef} className={`text-white font-basic tracking-[0.35rem] leading-tight whitespace-nowrap ${isCompactHeader ? "text-2xl" : "text-4xl"}`}>
              SALZZEIT
            </div>
            <div ref={line2Ref} className="text-gray-300 text-[7.3px] uppercase leading-tight whitespace-nowrap">
              Salzkammergut
            </div>
            <div ref={line3Ref} className="text-gray-300 text-[7.3px] uppercase leading-tight whitespace-nowrap">
              Connected History
            </div>
          </div>
        </div>

        {/* Buttons (top-right) */}
        <div className={`flex items-center pointer-events-auto ${isCompactHeader ? "space-x-2" : "space-x-4"}`}>
          {!snapStateDevice.isIphone && (
            <div
              className={`${circleButtonClass} ${isFullscreen ? "border-orange-400" : "border-white/30 bg-black/30 hover:border-white/50 hover:bg-black/50"}`}
              onClick={toggleFullscreen}
            >
              <img className={circleImgClass} src="UI/FullscreenIcon_.png" alt="FullScreen" />
            </div>
          )}

          {!snapOrbitLoaction.orbitClicked && !snapPlayerSwitch.charSwitchClicked && (
            <div
              className={`${circleButtonClass} ${isFp ? "border-orange-400" : "border-white/30 bg-black/30 hover:border-white/50 hover:bg-black/50"}`}
              onClick={handleFPClick}
            >
              <img className={circleImgClass} src="UI/FPButton_.png" alt="FirstPerson" />
            </div>
          )}

          {!snapOrbitLoaction.orbitClicked && snapInAreaRooms.inAreaRoomEntryRoom && (
            <div
              className={`${circleButtonClass} ${charClicked ? "border-orange-400" : "border-white/30 bg-black/30 hover:border-white/50 hover:bg-black/50"}`}
              onClick={handleCharClick}
            >
              <img className={circleImgClass} src="UI/CharButton2_.png" alt="Character" />
            </div>
          )}

          {!snapOrbitLoaction.orbitClicked && (
            <div
              className={`${circleButtonClass} border-orange-400 bg-black/30 hover:bg-black/50`}
              onClick={syncWorldToWaypointAndReturn}
            >
              <img className={isCompactHeader ? "w-4 h-4" : "w-5 h-5"} src="UI/GoPageIcon.png" alt="Zurück zu Navigation" />
            </div>
          )}
        </div>
      </header>
    </motion.div>
  );
}
